// Three files, because the country data is big and not everyone can wait for
// it:
//
//   country-lookup  the rules. No data, so it is safe to import anywhere.
//   country         loads the data up front, for callers that need an answer
//                   while rendering.
//   country-async   loads it only when called, so pages that do not need it
//                   right away do not ship it (#2751).
//
// Keep the rules in country-lookup. When they lived in the other two, a fix
// went into one copy and shipped doing nothing.
//
// This file holds the rules.

// Type only, so the build strips it and no data comes with it.
import type { Country } from 'countries-and-timezones'

// The backend sends `django_countries`' name for a country; this file's data
// comes from `countries-and-timezones`. The two packages agree on most names
// but not all - e.g. the backend's "Côte d'Ivoire" is this package's "Ivory
// Coast" for the same ISO code (CI). An exact-string match alone would treat
// that as an unknown country instead of finding it by code.
//
// Keyed by every backend name that diverges, mapped to the ISO code so the
// lookup still succeeds. Verified against django_countries 7.1 (the pinned
// backend version) and countries-and-timezones 3.2.2 (the pinned frontend
// version) - re-diff both country tables if either package is upgraded.
const COUNTRY_NAME_ALIASES: Record<string, string> = {
  'Brunei Darussalam': 'BN',
  'Bolivia (Plurinational State of)': 'BO',
  'Bonaire, Sint Eustatius and Saba': 'BQ',
  'Cocos (Keeling) Islands': 'CC',
  'Congo (the Democratic Republic of the)': 'CD',
  Congo: 'CG',
  "Côte d'Ivoire": 'CI',
  'Falkland Islands (Malvinas)': 'FK',
  'Micronesia (Federated States of)': 'FM',
  'United Kingdom of Great Britain and Northern Ireland': 'GB',
  'Iran (Islamic Republic of)': 'IR',
  "Korea (the Democratic People's Republic of)": 'KP',
  'Korea (the Republic of)': 'KR',
  "Lao People's Democratic Republic": 'LA',
  'Moldova (the Republic of)': 'MD',
  'Saint Martin (French part)': 'MF',
  'Palestine, State of': 'PS',
  'Russian Federation': 'RU',
  'Sint Maarten (Dutch part)': 'SX',
  'Syrian Arab Republic': 'SY',
  'Taiwan (Province of China)': 'TW',
  'Tanzania, the United Republic of': 'TZ',
  'Venezuela (Bolivarian Republic of)': 'VE',
  'Virgin Islands (British)': 'VG',
  'Virgin Islands (U.S.)': 'VI',
  'Viet Nam': 'VN',
}

export const findCountryByName = (
  countries: Record<string, Country>,
  countryName: string
): Country | undefined =>
  Object.values(countries).find((country) => country.name === countryName) ||
  countries[COUNTRY_NAME_ALIASES[countryName]]

export const countryCodeFromLocale = (locale: string): string =>
  locale?.split('-')[1]

// A timezone can cover several countries, and cannot say which on its own.
// The IP country breaks the tie; failing that, take the first.
export const pickCountryForTimezone = (
  candidates: Country[],
  countryFromIp?: Country
): Country | undefined => {
  if (candidates.length === 1) return candidates[0]
  return countryFromIp || candidates[0]
}
